var _Glyphicon, _Glyphicon2;
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';
import Glyphicon from '../Glyphicon/index.js';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function Carousel(_ref) {
  let {
    onSelect = () => {},
    onDragStart = () => {},
    onDragEnd = () => {},
    onDragMove = () => {},
    onNextButtonClick = () => {},
    onPrevButtonClick = () => {},
    onStaticClick = () => {},
    onSettle = () => {},
    onReady = () => {},
    onLoad = () => {},
    initialIndex,
    className,
    children,
    draggable = false,
    fullWidth,
    fullWidthScroll,
    fullWidthScrollXs = false,
    singleImageMode = false,
    fullHeight,
    lazyLoad,
    prevNextButtons = true,
    cellWidth,
    scrollItemsCount,
    stackedTileWidth,
    visibilityThreshold = 1,
    ...rest
  } = _ref;
  const carouselContainerRef = useRef();
  const sliderIndex = useRef(0);
  const prevSliderIndex = useRef(0);
  let cells = [];
  let selectedCells = [];
  const selectedIndices = useRef([]);
  const makeCells = () => {
    cells = Array.from(carouselContainerRef.current.children);
  };
  const getSelectedIndices = () => {
    if (selectedCells.length > 0) {
      return selectedCells.map(cell => cells.indexOf(cell));
    }
    return null;
  };

  // get list of visible elements in the carousel
  const getVisibleElements = element => {
    const rect = element.getBoundingClientRect();
    const visibleElements = Array.from(element.children).filter(child => {
      const childRect = child.getBoundingClientRect();
      let bufferWidth = 0;
      if (visibilityThreshold > 0 && visibilityThreshold < 1) {
        bufferWidth = childRect.width * (1 - visibilityThreshold);
      }

      // By default (visibilityThreshold = 1 (100%)), an element will be considered as visible when it is displayed 100% in the carousel view.
      // Changing visibilityThreshold will alter the percentage of displayed width at which an element is considered visible.
      // Example: visibilityThreshold = 0.9 (90%) => If an element is displayed at least 90% of its width, it's considered visible.
      return rect.left <= childRect.left + bufferWidth && rect.right >= childRect.right - bufferWidth;
    });
    return visibleElements;
  };
  const updateSelectedCells = element => {
    const visibleElements = getVisibleElements(element);
    if (visibleElements.length > 0) {
      selectedCells = visibleElements;
    }
  };
  const handleSettle = () => {
    if (carouselContainerRef?.current) {
      const prevIndex = prevSliderIndex.current;
      const index = sliderIndex.current;
      updateSelectedCells(carouselContainerRef.current);
      selectedIndices.current = getSelectedIndices();
      const indexDirection = direction => {
        const width = carouselContainerRef?.current?.clientWidth;
        onSettle(index, direction, width, selectedCells, selectedIndices.current);
      };
      if (index - prevIndex > 0) {
        indexDirection('right');
      } else if (index - prevIndex < 0) {
        indexDirection('left');
      }
    }
  };
  const debounceHandleSettle = _debounce(handleSettle, 100);
  const bindEvents = () => {
    carouselContainerRef.current.addEventListener('select', onSelect);
    carouselContainerRef.current.addEventListener('dragStart', onDragStart);
    carouselContainerRef.current.addEventListener('dragEnd', onDragEnd);
    carouselContainerRef.current.addEventListener('dragMove', onDragMove);
  };
  const unbindEvents = () => {
    if (carouselContainerRef?.current) {
      carouselContainerRef.current.removeEventListener('select', onSelect);
      carouselContainerRef.current.removeEventListener('dragStart', onDragStart);
      carouselContainerRef.current.removeEventListener('dragEnd', onDragEnd);
      carouselContainerRef.current.removeEventListener('dragMove', onDragMove);
    }
  };
  useEffect(() => {
    updateSelectedCells(carouselContainerRef.current);
    let scrollObserver;
    if (carouselContainerRef?.current && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const optionsViewPort = {
        root: carouselContainerRef.current,
        rootMargin: '100px 0px 100px 0px',
        threshold: visibilityThreshold
      };
      scrollObserver = new IntersectionObserver(debounceHandleSettle, optionsViewPort);
      const childElements = Array.from(carouselContainerRef.current.children);
      if (childElements && childElements.length > 0) {
        childElements.forEach(e => scrollObserver.observe(e));
      }
    }
    bindEvents();
    if (onReady) {
      makeCells();
      selectedIndices.current = getSelectedIndices();
      onReady(initialIndex, selectedCells, selectedIndices.current);
    }
    if (onLoad) {
      onLoad(initialIndex);
    }
    return () => {
      if (scrollObserver) {
        scrollObserver.disconnect();
      }
      unbindEvents();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Get the nth (distance) sibling of an element
   * @param {HTMLElement} element: The current element
   * @param {number} distance: The distance from the sibling to the current element
   * @param {'left' | 'right'} direction: The traversal direction.
   *        'left': Go backward (previousElementSibling)
   *        'right': Go forward (nextElementSibling)
   * @returns {HTMLElement | null}: The sibling of the element, or null if there are no siblings
   */
  const getSiblingElement = (element, distance, direction) => {
    if (!(element instanceof HTMLElement)) {
      return null;
    }
    let sibling = element;
    const siblingField = direction === 'right' ? 'nextElementSibling' : 'previousElementSibling';
    for (let i = 0; i < distance; i++) {
      if (sibling[siblingField]) {
        sibling = sibling[siblingField];
      }
    }

    // return null if there are no siblings
    return sibling !== element ? sibling : null;
  };
  const getPrevElement = list => {
    const sibling = list ? list[0].previousElementSibling : null;
    if (fullWidthScroll || fullWidthScrollXs) {
      const amountOfItems = scrollItemsCount > 0 ? scrollItemsCount : list?.length;
      return getSiblingElement(list[0], amountOfItems, 'left');
    }
    const thirdSibling = sibling?.previousElementSibling?.previousElementSibling;
    if (thirdSibling instanceof HTMLElement) {
      return thirdSibling;
    }
    return null;
  };
  const getNextElement = list => {
    const sibling = list[list.length - 1].nextElementSibling;
    if ((fullWidthScroll || fullWidthScrollXs) && sibling) {
      const amountOfItems = scrollItemsCount > 0 ? scrollItemsCount : list?.length;
      return getSiblingElement(list[0], amountOfItems, 'right');
    }
    const thirdSibling = sibling?.nextElementSibling?.nextElementSibling;
    if (thirdSibling instanceof HTMLElement) {
      return thirdSibling;
    }
    return null;
  };
  const updateIndex = direction => {
    if (direction === 'left') {
      prevSliderIndex.current = sliderIndex.current;
      sliderIndex.current -= 1;
      onPrevButtonClick();
    }
    if (direction === 'right') {
      prevSliderIndex.current = sliderIndex.current;
      sliderIndex.current += 1;
      onNextButtonClick();
    }
  };
  const usePosition = ref => {
    const [prevElement, setPrevElement] = useState(null);
    const [nextElement, setNextElement] = useState(null);
    useEffect(() => {
      const element = ref.current;
      const update = _debounce(() => {
        const visibleElements = getVisibleElements(element);
        if (visibleElements.length > 0) {
          setPrevElement(getPrevElement(visibleElements));
          setNextElement(getNextElement(visibleElements));
        }
      }, 100);
      update();
      let scrollObserver;
      if (element && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        const optionsViewPort = {
          root: element,
          rootMargin: '100px 0px 100px 0px',
          threshold: visibilityThreshold
        };
        scrollObserver = new IntersectionObserver(update, optionsViewPort);
        const childElements = Array.from(element.children);
        if (childElements && childElements.length > 0) {
          childElements.forEach(e => scrollObserver.observe(e));
        }
      }
      return () => {
        if (scrollObserver) {
          scrollObserver.disconnect();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, children]);
    const scrollToElement = useCallback((element, direction) => {
      if (direction) updateIndex(direction);
      const currentNode = ref.current;
      if (!currentNode || !element) return;
      let newScrollPosition;
      if (fullWidthScroll || fullWidthScrollXs) {
        newScrollPosition = element.offsetLeft;
      } else {
        newScrollPosition = element.offsetLeft + element.getBoundingClientRect().width / 2 - currentNode.getBoundingClientRect().width / 2;
      }
      currentNode.scroll({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }, [ref]);
    const scrollToFirst = useCallback(() => {
      const currentNode = ref.current;
      if (!currentNode || !currentNode.children.length) return;
      scrollToElement(currentNode.children[0]);
    }, [ref, scrollToElement]);
    const scrollToLast = useCallback(() => {
      const currentNode = ref.current;
      if (!currentNode || !currentNode.children.length) return;
      scrollToElement(currentNode.children[currentNode.children.length - 1]);
    }, [ref, scrollToElement]);
    const scrollRight = useCallback(function (isButtonClick) {
      if (isButtonClick === void 0) {
        isButtonClick = false;
      }
      if (!nextElement && isButtonClick && fullWidthScrollXs) {
        scrollToFirst();
      } else {
        scrollToElement(nextElement, 'right');
      }
    }, [scrollToElement, nextElement, scrollToFirst]);
    const scrollLeft = useCallback(function (isButtonClick) {
      if (isButtonClick === void 0) {
        isButtonClick = false;
      }
      if (!prevElement && isButtonClick && fullWidthScrollXs) {
        scrollToLast();
      } else {
        scrollToElement(prevElement, 'left');
      }
    }, [scrollToElement, prevElement, scrollToLast]);
    useEffect(() => {
      if (!fullWidthScrollXs) return;
      const element = ref.current;
      let startX = 0;
      let startY = 0;
      let isVerticalScroll;
      let isDragging = false;
      const onTouchStart = e => {
        if (e.touches && e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isVerticalScroll = undefined;
        }
      };
      const onTouchMove = e => {
        if (e.touches && e.touches.length === 1) {
          const deltaX = Math.abs(e.touches[0].clientX - startX);
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          if (isVerticalScroll === undefined) {
            isVerticalScroll = deltaY > deltaX;
          }
          if (!isVerticalScroll) {
            e.preventDefault();
          }
        }
      };
      const onTouchEnd = e => {
        let endX = 0;
        if (e.changedTouches && e.changedTouches.length === 1) {
          endX = e.changedTouches[0].clientX;
        }
        const deltaX = endX - startX;

        // Minimum horizontal swipe distance (in pixels) required to trigger scrolling
        if (Math.abs(deltaX) > 40 && !isVerticalScroll) {
          if (deltaX > 0) {
            scrollLeft();
          } else {
            scrollRight();
          }
        }
        startX = 0;
        startY = 0;
        isVerticalScroll = undefined;
      };
      const onMouseDown = e => {
        startX = e.clientX;
        isDragging = false;
      };
      const onMouseUp = e => {
        const endX = e.clientX;
        const deltaX = endX - startX;

        // Minimum horizontal drag distance (in pixels) required to trigger scrolling
        if (Math.abs(deltaX) > 40) {
          isDragging = true;
          if (deltaX > 0) {
            scrollLeft();
          } else {
            scrollRight();
          }
        }
        startX = 0;
      };

      // Prevent click event if dragging
      const onClick = e => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // eslint-disable-next-line github/require-passive-events
      element.addEventListener('touchmove', onTouchMove, {
        passive: false
      });
      element.addEventListener('touchstart', onTouchStart, {
        passive: true
      });
      element.addEventListener('touchend', onTouchEnd, {
        passive: true
      });
      element.addEventListener('mousedown', onMouseDown);
      element.addEventListener('mouseup', onMouseUp);
      element.addEventListener('click', onClick);

      // eslint-disable-next-line consistent-return
      return () => {
        if (element) {
          element.removeEventListener('touchstart', onTouchStart);
          element.removeEventListener('touchend', onTouchEnd);
          element.removeEventListener('touchmove', onTouchMove);
          element.removeEventListener('mousedown', onMouseDown);
          element.removeEventListener('mouseup', onMouseUp);
          element.removeEventListener('click', onClick);
        }
      };
    }, [ref, scrollLeft, scrollRight]);
    return {
      hasItemsOnLeft: prevElement !== null,
      hasItemsOnRight: nextElement !== null,
      scrollRight,
      scrollLeft
    };
  };
  const {
    hasItemsOnLeft,
    hasItemsOnRight,
    scrollRight,
    scrollLeft
  } = usePosition(carouselContainerRef);
  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (!child) return null;
    let childProps = child.props;
    if (child.type && child.type === 'img' && !!lazyLoad) {
      const {
        src,
        ...restProps
      } = child.props;
      childProps = {
        ...restProps,
        key: 'carousel-element-' + index,
        'data-lazyload': src,
        src: null,
        loading: 'lazy'
      };
    }
    let inlineStyles = null;
    if (cellWidth && !singleImageMode) {
      inlineStyles = {
        width: cellWidth,
        flex: `0 0 ${cellWidth}`,
        position: 'relative'
      };
      if (!!childProps.stacked && stackedTileWidth) {
        inlineStyles.width = stackedTileWidth;
      }
    }
    if (fullWidthScrollXs && singleImageMode) {
      const originalOnClick = childProps.onClick;
      const handleClick = e => {
        const container = carouselContainerRef.current;
        const visibleElements = getVisibleElements(container);
        const isVisible = visibleElements.some(el => el === e.target.closest('.carousel-cell'));

        // if the clicked element is not visible (90% width in viewport), scroll to it
        if (!isVisible) {
          if (hasItemsOnRight) {
            scrollRight();
          } else {
            scrollLeft();
          }
        } else {
          // else execute child's onClick function if it exists
          originalOnClick?.(e);
        }
      };
      childProps = {
        ...childProps,
        onClick: handleClick
      };
    }
    const classNames = clsx({
      'carousel-cell': true,
      'carousel-full-width': fullWidth,
      'single-image-carousel': fullWidthScrollXs && singleImageMode,
      'display-flex': fullHeight,
      'justify-content-center': fullHeight
    });
    return /*#__PURE__*/_jsx("div", {
      className: classNames,
      style: inlineStyles,
      children: /*#__PURE__*/React.cloneElement(child, childProps)
    });
  });
  delete rest.options;
  delete rest.disableImagesLoaded;
  const buttonClassNames = 'btn-default carousel-container carousel-nav-button hidden-xs padding-0';
  return /*#__PURE__*/_jsxs("div", {
    "data-cmp": "carousel",
    className: clsx(className, 'carousel-container', {
      'is-draggable': draggable,
      'padding-left-4 padding-left-sm-0': fullWidthScrollXs && singleImageMode && hasItemsOnRight,
      // Add padding left on xs if there are items on the right
      'padding-right-4 padding-right-sm-0': fullWidthScrollXs && singleImageMode && !hasItemsOnRight // Add padding right on xs if there are no items on the right
    }),
    ...rest,
    children: [/*#__PURE__*/_jsx("div", {
      ref: carouselContainerRef,
      className: clsx('carousel-container-inner', {
        'no-scroll-x': fullWidthScrollXs && singleImageMode
      }),
      children: childrenWithProps
    }), /*#__PURE__*/_jsx("button", {
      type: "button",
      className: clsx(buttonClassNames, 'previous'),
      onClick: () => scrollLeft(true),
      "aria-label": "Previous slide",
      disabled: fullWidthScrollXs ? !prevNextButtons || !hasItemsOnLeft && !hasItemsOnRight : !hasItemsOnLeft || !prevNextButtons,
      autoComplete: "off",
      children: _Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
        className: "text-center text-size-700",
        glyph: "menu-left"
      }))
    }), /*#__PURE__*/_jsx("button", {
      type: "button",
      className: clsx(buttonClassNames, 'next'),
      onClick: () => scrollRight(true),
      "aria-label": "Next slide",
      disabled: fullWidthScrollXs ? !prevNextButtons || !hasItemsOnLeft && !hasItemsOnRight : !hasItemsOnRight || !prevNextButtons,
      autoComplete: "off",
      children: _Glyphicon2 || (_Glyphicon2 = /*#__PURE__*/_jsx(Glyphicon, {
        className: "text-center text-size-700",
        glyph: "menu-right"
      }))
    })]
  });
}
export default Carousel;